import React from 'react'
import { Button, Input, InputNumber, Space, Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import type { BenefitPlanTier } from '../../lib/api'

const { Text } = Typography

/**
 * thankyou_v2（批次 8.2）礼遇分档子表编辑器（设计契约 admin-benefit-config-two-level.html）。
 *
 * 语义（Contract Pack 逐字）：areaFrom 含、areaTo 不含（空 = 无上限）；
 * 首档下限须 0、末档上限须留空、相邻档须无缝（上一档 areaTo === 下一档 areaFrom）。
 * 前端即时校验重叠 / 空档并红字提示；后端 benefit-plans.ts::validateTiers 为权威二次闸门。
 *
 * 作为 antd Form 受控件使用：<Form.Item name="tiers"><TierTable /></Form.Item>
 * —— 故实现 value / onChange 协议，不自持 state。
 */
export interface TierRow {
  areaFrom: number | null
  areaTo: number | null
  benefitContent: string
  couponValue: number | null
  /** 编辑既有行时保留后端 id，便于 diff 展示（提交时随 body 一并回传） */
  id?: number
}

/** BenefitPlanTier[]（后端返回）→ 表单行 */
export const toTierRows = (tiers?: BenefitPlanTier[] | null): TierRow[] =>
  (tiers ?? []).map((t) => ({
    id: t.id,
    areaFrom: t.areaFrom == null ? null : Number(t.areaFrom),
    areaTo: t.areaTo == null ? null : Number(t.areaTo),
    benefitContent: t.benefitContent ?? '',
    couponValue: t.couponValue == null ? null : Number(t.couponValue),
  }))

/** 表单行 → 提交 body 的 tiers[]（sortOrder 按当前顺序） */
export const fromTierRows = (rows: TierRow[]): BenefitPlanTier[] =>
  rows.map((r, i) => ({
    ...(r.id != null ? { id: r.id } : {}),
    areaFrom: r.areaFrom ?? 0,
    areaTo: r.areaTo,
    benefitContent: r.benefitContent.trim(),
    couponValue: r.couponValue,
    sortOrder: i,
  }))

/**
 * 分档即时校验：返回红字提示数组（空 = 通过）。
 * 覆盖设计契约的四类拦截：首档下限、末档上限、区间重叠、区间空档，另加必填与递增。
 */
export const validateTierRows = (rows: TierRow[]): string[] => {
  const errs: string[] = []
  if (rows.length === 0) {
    errs.push('按面积分档时至少须有一档')
    return errs
  }
  rows.forEach((r, i) => {
    if (r.areaFrom == null) errs.push(`第 ${i + 1} 档：面积下限必填`)
    if (!r.benefitContent.trim()) errs.push(`第 ${i + 1} 档：礼遇内容必填`)
    if (r.areaTo != null && r.areaFrom != null && Number(r.areaTo) <= Number(r.areaFrom)) {
      errs.push(`第 ${i + 1} 档：面积上限须大于下限`)
    }
  })
  if (Number(rows[0].areaFrom) !== 0) {
    errs.push('首档面积下限须为 0')
  }
  if (rows[rows.length - 1].areaTo != null) {
    errs.push('末档面积上限须留空（= 无上限）')
  }
  for (let i = 0; i < rows.length - 1; i += 1) {
    const cur = rows[i]
    const next = rows[i + 1]
    if (cur.areaTo == null) {
      errs.push(`第 ${i + 1} 档上限留空即无上限，其后不得再有分档`)
      continue
    }
    if (next.areaFrom == null) continue
    if (Number(cur.areaTo) > Number(next.areaFrom)) {
      errs.push(`第 ${i + 1} 档与第 ${i + 2} 档区间重叠（${cur.areaTo} > ${next.areaFrom}）`)
    } else if (Number(cur.areaTo) < Number(next.areaFrom)) {
      errs.push(`第 ${i + 1} 档与第 ${i + 2} 档之间存在空档（${cur.areaTo} ~ ${next.areaFrom}）`)
    }
  }
  return errs
}

interface Props {
  value?: TierRow[]
  onChange?: (rows: TierRow[]) => void
  disabled?: boolean
}

const emptyRow = (): TierRow => ({
  areaFrom: null,
  areaTo: null,
  benefitContent: '',
  couponValue: null,
})

const TierTable: React.FC<Props> = ({ value, onChange, disabled }) => {
  const rows = value ?? []
  const emit = (next: TierRow[]) => onChange?.(next)

  const patchRow = (idx: number, part: Partial<TierRow>) => {
    emit(rows.map((r, i) => (i === idx ? { ...r, ...part } : r)))
  }

  const columns: ColumnsType<TierRow> = [
    {
      title: '面积下限(含)',
      dataIndex: 'areaFrom',
      key: 'areaFrom',
      width: 130,
      render: (_: unknown, r, idx) => (
        <InputNumber
          min={0}
          value={r.areaFrom}
          disabled={disabled}
          placeholder="0"
          style={{ width: '100%', minHeight: 32 }}
          onChange={(v) => patchRow(idx, { areaFrom: v == null ? null : Number(v) })}
        />
      ),
    },
    {
      title: '面积上限(不含,空=无上限)',
      dataIndex: 'areaTo',
      key: 'areaTo',
      width: 170,
      render: (_: unknown, r, idx) => (
        <InputNumber
          min={0}
          value={r.areaTo}
          disabled={disabled}
          placeholder="留空 = 无上限"
          style={{ width: '100%', minHeight: 32 }}
          onChange={(v) => patchRow(idx, { areaTo: v == null ? null : Number(v) })}
        />
      ),
    },
    {
      title: '礼遇内容',
      dataIndex: 'benefitContent',
      key: 'benefitContent',
      render: (_: unknown, r, idx) => (
        <Input
          value={r.benefitContent}
          disabled={disabled}
          maxLength={500}
          placeholder="例：面值10,000元餐饮代金券"
          style={{ minHeight: 32 }}
          onChange={(e) => patchRow(idx, { benefitContent: e.target.value })}
        />
      ),
    },
    {
      title: '券面值',
      dataIndex: 'couponValue',
      key: 'couponValue',
      width: 130,
      render: (_: unknown, r, idx) => (
        <InputNumber
          min={0}
          value={r.couponValue}
          disabled={disabled}
          placeholder="10000"
          style={{ width: '100%', minHeight: 32 }}
          onChange={(v) => patchRow(idx, { couponValue: v == null ? null : Number(v) })}
        />
      ),
    },
    {
      title: '',
      key: 'del',
      width: 64,
      render: (_: unknown, __: TierRow, idx) => (
        <Button
          size="small"
          danger
          type="text"
          icon={<DeleteOutlined />}
          disabled={disabled}
          aria-label={`删除第 ${idx + 1} 档`}
          style={{ minHeight: 32 }}
          onClick={() => emit(rows.filter((_r, i) => i !== idx))}
        >
          删除
        </Button>
      ),
    },
  ]

  const errs = validateTierRows(rows)

  return (
    <div
      style={{
        border: '1px dashed #1A873B',
        borderRadius: 6,
        padding: 8,
        background: '#FCFEFC',
      }}
    >
      <Space style={{ marginBottom: 6 }} wrap>
        <Text strong style={{ fontSize: 12 }}>
          礼遇分档子表（benefitContentSource = 按面积分档 时必填）
        </Text>
        <Button
          size="small"
          icon={<PlusOutlined />}
          disabled={disabled}
          style={{ minHeight: 32 }}
          onClick={() => {
            // 新增档默认接在末档之后：下限 = 上一档上限（末档上限留空时从 0 起）
            const last = rows[rows.length - 1]
            emit([...rows, { ...emptyRow(), areaFrom: last?.areaTo ?? (rows.length === 0 ? 0 : null) }])
          }}
        >
          + 增加分档
        </Button>
      </Space>
      <Table<TierRow>
        rowKey={(_r, idx) => String(idx)}
        size="small"
        columns={columns}
        dataSource={rows}
        pagination={false}
        locale={{ emptyText: '尚无分档，点击「+ 增加分档」' }}
      />
      {/* 前端即时校验红字提示（后端 validateTiers 为权威闸门） */}
      {errs.length > 0 && (
        <div style={{ marginTop: 6 }}>
          {errs.map((e) => (
            <div key={e} style={{ color: '#E74C3C', fontSize: 12, lineHeight: 1.7 }}>
              · {e}
            </div>
          ))}
        </div>
      )}
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
        区间不得重叠或留空档；首档下限须 0、末档上限须留空（后端 validateTiers 二次拦截）。
      </Text>
    </div>
  )
}

export default TierTable
